import {
  hasLikelyDuplicateTranscription,
  normalizeTranscriptionTextForComparison,
} from "@/lib/livekitTranscriptionDedup";
import {
  LIVEKIT_PUBLISH_ON_BEHALF_ATTRIBUTE,
  normalizeLiveKitTranscriptionStreams,
} from "@/lib/livekitTranscriptionStreams";
import { createLogger } from "@/lib/logger";
import {
  useRemoteParticipants,
  useRoomContext,
  useTrackTranscription,
  useTranscriptions,
  useVoiceAssistant,
} from "@livekit/components-react";
import { useEffect, useMemo, useState } from "react";
import useLocalMicTrack from "./useLocalMicTrack";

const logger = createLogger("useCombinedTranscriptions");
const USER_TRANSCRIPTION_CROSS_TRANSPORT_WINDOW_MS = 2500;
// Slightly above the Soniox Flash-Lite agent merge ceiling (4s) so the UI
// bubble matches the tutor turn without gluing unrelated later turns.
const USER_TRANSCRIPT_MERGE_WINDOW_MS = 5000;

type TranscriptSegmentLike = {
  id?: string;
  text?: string;
  firstReceivedTime?: number;
  streamTimestamp?: number;
  final?: boolean;
  isFinal?: boolean;
};

type CompletedUserMessage = TranscriptSegmentLike & {
  id: string;
  role: "user";
  isCompleted: true;
  firstReceivedTime: number;
  providerSegmentId: string;
};

type CombinedTranscriptionMessage = TranscriptSegmentLike & {
  id: string;
  role: "assistant" | "user";
  text: string;
  firstReceivedTime: number;
  source?: "text-stream" | "track-transcription";
  isCompleted?: boolean;
  mergedTranscriptIds?: string[];
  mergedLastReceivedTime?: number;
  providerSegmentId?: string;
};

function getFiniteTimestamp(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function getUserSegmentKey(segment: TranscriptSegmentLike): string {
  return String(segment.id || segment.text || "user_segment");
}

function hasCanonicalUserCounterpart(
  fallback: TranscriptSegmentLike & {
    firstReceivedTime: number;
    providerSegmentId?: string | null;
  },
  canonicalMessages: CombinedTranscriptionMessage[]
): boolean {
  const providerSegmentId = String(fallback.providerSegmentId || fallback.id || "");

  return canonicalMessages.some((canonical) => {
    if (providerSegmentId && canonical.id === providerSegmentId) {
      return true;
    }

    const arrivalDelta = Math.abs(canonical.firstReceivedTime - fallback.firstReceivedTime);
    if (arrivalDelta > USER_TRANSCRIPTION_CROSS_TRANSPORT_WINDOW_MS) {
      return false;
    }

    const fallbackText = normalizeTranscriptionTextForComparison(fallback.text);
    const canonicalText = normalizeTranscriptionTextForComparison(canonical.text);
    return Boolean(fallbackText) && fallbackText === canonicalText;
  });
}

function isActiveUserTranscript(message: CombinedTranscriptionMessage): boolean {
  return message.id.startsWith("active_user_");
}

function isMergeableUserTranscript(message: CombinedTranscriptionMessage): boolean {
  return (
    message.role === "user" &&
    !isActiveUserTranscript(message) &&
    (message.isFinal === true ||
      message.final === true ||
      message.isCompleted === true ||
      // Text-stream learner segments often keep a stale isFinal=false from the
      // stream-open attributes; still merge them across short pauses.
      message.source === "text-stream")
  );
}

function userTranscriptEndTime(message: CombinedTranscriptionMessage): number {
  return message.mergedLastReceivedTime ?? message.firstReceivedTime;
}

function canMergeUserTranscripts(
  previous: CombinedTranscriptionMessage,
  message: CombinedTranscriptionMessage
): boolean {
  if (!isMergeableUserTranscript(previous) || !isMergeableUserTranscript(message)) {
    return false;
  }
  const gapMs = message.firstReceivedTime - userTranscriptEndTime(previous);
  return gapMs >= 0 && gapMs <= USER_TRANSCRIPT_MERGE_WINDOW_MS;
}

function mergeConsecutiveUserTranscripts(
  messages: CombinedTranscriptionMessage[]
): CombinedTranscriptionMessage[] {
  // Track-transcription active_user_* placeholders can sit between Soniox
  // finals and block consecutive merge. Keep only a trailing live preview.
  const trailingActive =
    messages.length > 0 && isActiveUserTranscript(messages[messages.length - 1])
      ? messages[messages.length - 1]
      : null;

  const merged = messages.reduce<CombinedTranscriptionMessage[]>((acc, message) => {
    if (isActiveUserTranscript(message)) {
      return acc;
    }

    const previous = acc[acc.length - 1];
    if (previous && canMergeUserTranscripts(previous, message)) {
      const previousIds = previous.mergedTranscriptIds ?? [previous.id];
      const messageIds = message.mergedTranscriptIds ?? [message.id];
      acc[acc.length - 1] = {
        ...previous,
        text: `${previous.text.trim()} ${message.text.trim()}`.trim(),
        mergedTranscriptIds: [...previousIds, ...messageIds],
        mergedLastReceivedTime: message.mergedLastReceivedTime ?? message.firstReceivedTime,
        final: message.final ?? previous.final,
        isFinal: message.isFinal ?? previous.isFinal,
        isCompleted: message.isCompleted ?? previous.isCompleted,
      };
      return acc;
    }

    acc.push(message);
    return acc;
  }, []);

  if (trailingActive) {
    merged.push(trailingActive);
  }
  return merged;
}

export default function useCombinedTranscriptions() {
  const { agent, agentTranscriptions } = useVoiceAssistant();
  const room = useRoomContext();
  const remoteParticipants = useRemoteParticipants();
  const textStreamTranscriptions = useTranscriptions();

  const micTrackRef = useLocalMicTrack();
  const { segments: userTranscriptions } = useTrackTranscription(micTrackRef);

  const [completedUserMessages, setCompletedUserMessages] = useState<CompletedUserMessage[]>([]);

  const workerIdentities = useMemo(
    () =>
      remoteParticipants
        .filter(
          (participant) =>
            participant.attributes?.[LIVEKIT_PUBLISH_ON_BEHALF_ATTRIBUTE] === agent?.identity
        )
        .map((participant) => participant.identity),
    [agent?.identity, remoteParticipants]
  );

  const normalizedTextStreamMessages = useMemo(() => {
    const normalized = normalizeLiveKitTranscriptionStreams(textStreamTranscriptions, {
      localParticipantIdentity: room.localParticipant.identity,
      agentIdentity: agent?.identity,
      workerIdentities,
    });

    return normalized.flatMap((message) => {
      const firstReceivedTime = getFiniteTimestamp(message.streamTimestamp);
      return firstReceivedTime === undefined ? [] : [{ ...message, firstReceivedTime }];
    });
  }, [agent?.identity, room.localParticipant.identity, textStreamTranscriptions, workerIdentities]);

  const legacyAssistantTranscriptions = agentTranscriptions as TranscriptSegmentLike[];
  const activeUserSegment =
    userTranscriptions.length > 0
      ? (userTranscriptions[userTranscriptions.length - 1] as TranscriptSegmentLike)
      : null;
  const activeUserText = String(activeUserSegment?.text || "");
  const activeUserSegmentKey = activeUserSegment ? getUserSegmentKey(activeUserSegment) : null;
  const activeUserFirstReceivedTime = getFiniteTimestamp(activeUserSegment?.firstReceivedTime);

  useEffect(() => {
    if (
      !activeUserSegment ||
      !activeUserSegmentKey ||
      activeUserFirstReceivedTime === undefined ||
      !activeUserText.trim()
    ) {
      return;
    }

    const timer = setTimeout(() => {
      setCompletedUserMessages((current) => {
        const existingIndex = current.findIndex(
          (message) => message.providerSegmentId === activeUserSegmentKey
        );
        const completedMessage: CompletedUserMessage = {
          ...activeUserSegment,
          id: `user_${activeUserSegmentKey}`,
          text: activeUserText,
          role: "user",
          isCompleted: true,
          firstReceivedTime: activeUserFirstReceivedTime,
          providerSegmentId: activeUserSegmentKey,
        };

        if (existingIndex < 0) {
          logger.info("Adding completed user message", { text: activeUserText });
          return [...current, completedMessage];
        }

        if (
          current[existingIndex].text === completedMessage.text &&
          current[existingIndex].firstReceivedTime === completedMessage.firstReceivedTime
        ) {
          return current;
        }

        const next = [...current];
        next[existingIndex] = completedMessage;
        return next;
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [activeUserFirstReceivedTime, activeUserSegment, activeUserSegmentKey, activeUserText]);

  const combinedTranscriptions = useMemo(() => {
    const textStreamMessageIds = new Set(
      normalizedTextStreamMessages.map((message) => String(message.id))
    );
    const textStreamMessageText = new Set(
      normalizedTextStreamMessages.map((message) => String(message.text || "").trim())
    );
    const textStreamAssistantMessages = normalizedTextStreamMessages.filter(
      (message) => message.role === "assistant"
    );
    const textStreamUserMessages = normalizedTextStreamMessages.filter(
      (message) => message.role === "user"
    );

    const showActiveSegment =
      activeUserSegment &&
      activeUserText.trim().length > 0 &&
      activeUserFirstReceivedTime !== undefined &&
      !completedUserMessages.some((message) => message.providerSegmentId === activeUserSegmentKey);
    const currentUserTranscription = showActiveSegment
      ? [
          {
            ...activeUserSegment,
            id: `active_user_${activeUserSegmentKey}`,
            text: activeUserText,
            role: "user" as const,
            firstReceivedTime: activeUserFirstReceivedTime as number,
            providerSegmentId: activeUserSegmentKey,
          },
        ]
      : [];

    const legacyAssistantMessages = legacyAssistantTranscriptions
      .filter((message) => {
        const id = String(message.id || "");
        const text = String(message.text || "").trim();
        return (
          !textStreamMessageIds.has(id) &&
          !textStreamMessageText.has(text) &&
          !hasLikelyDuplicateTranscription(
            {
              id,
              text,
              final: message.final,
              isFinal: typeof message.final === "boolean" ? message.final : undefined,
            },
            textStreamAssistantMessages
          )
        );
      })
      .map((message) => {
        const firstReceivedTime = getFiniteTimestamp(message.firstReceivedTime);
        if (firstReceivedTime === undefined) return null;

        return {
          ...message,
          role: "assistant" as const,
          source: "track-transcription" as const,
          isFinal: typeof message.final === "boolean" ? message.final : undefined,
          firstReceivedTime,
        };
      })
      .filter((message): message is NonNullable<typeof message> => message !== null);

    // Agents 1.x publishes both text streams and the deprecated RTC
    // transcription transport. Suppress only a fallback that has a canonical
    // counterpart; a missing later text stream must not hide that learner turn.
    const legacyUserMessages = [...completedUserMessages, ...currentUserTranscription]
      .filter((message) => !hasCanonicalUserCounterpart(message, textStreamUserMessages))
      .map((message) => ({
        ...message,
        role: "user" as const,
        source: "track-transcription" as const,
      }));

    const orderedMessages = [
      ...textStreamAssistantMessages,
      ...textStreamUserMessages,
      ...legacyAssistantMessages,
      ...legacyUserMessages,
    ].sort((a, b) => a.firstReceivedTime - b.firstReceivedTime) as CombinedTranscriptionMessage[];

    return mergeConsecutiveUserTranscripts(orderedMessages);
  }, [
    activeUserFirstReceivedTime,
    activeUserSegment,
    activeUserSegmentKey,
    activeUserText,
    completedUserMessages,
    legacyAssistantTranscriptions,
    normalizedTextStreamMessages,
  ]);

  return combinedTranscriptions;
}
